import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { industries } from "@/data/content";

export default function Industries() {
    return (
        <div>
            <Seo
                title="Industry Sectors & Verticals | Vantage Crest Research"
                description="Deep domain expertise across BFSI, Pharma, IT, Retail and Manufacturing — research-led executive search and talent intelligence by sector."
            />

            <section
                className="relative overflow-hidden bg-[#0C0C0C] pt-40 pb-24"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 20% 15%, rgba(18,18,18,0.8) 0%, rgba(6,6,6,0.95) 55%, #060606 100%)",
                }}
            >
                <div className="absolute inset-0 grid-lines-dark pointer-events-none" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <Eyebrow dark>Industries</Eyebrow>
                    <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-[#F7F5F1] mt-6 max-w-4xl">
                        Diversified by design.
                        <br />
                        <span className="text-[#D8BC7A]">Deep wherever it matters.</span>
                    </h1>
                    <p className="mt-8 text-lg text-[#A6A69C] leading-relaxed max-w-2xl">
                        We're not limited to a fixed list of sectors — our research method
                        adapts to any industry. These verticals show the depth of our
                        delivery experience; our coverage goes well beyond them.
                    </p>
                </div>
            </section>

            <section className="bg-white">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    {industries.map((ind, i) => (
                        <div
                            key={ind.id}
                            id={ind.id}
                            data-testid={`industry-${ind.id}`}
                            className="grid lg:grid-cols-12 gap-8 py-16 sm:py-20 border-b border-[#E7E4DC] last:border-b-0 items-start"
                        >
                            <Reveal className="lg:col-span-4">
                                <div className="flex items-center gap-4">
                                    <span className="font-mono-accent text-sm text-[#C9A24B] tracking-widest">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="h-px flex-1 bg-[#E7E4DC]" />
                                </div>
                                <div className="mt-7 flex items-center gap-4">
                                    <span className="shrink-0 w-12 h-12 border border-[#C9A24B]/50 flex items-center justify-center">
                                        <ind.icon size={22} className="text-[#C9A24B]" strokeWidth={1.5} />
                                    </span>
                                    <div>
                                        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#141414]">
                                            {ind.name}
                                        </h2>
                                        <p className="text-xs text-[#77776E] mt-1">{ind.full}</p>
                                    </div>
                                </div>
                            </Reveal>
                            <div className="lg:col-span-8">
                                <Reveal delay={0.08}>
                                    <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#C9A24B]">
                                        {ind.tagline}
                                    </p>
                                    <p className="mt-4 text-base text-[#4E4E48] leading-relaxed">
                                        {ind.paragraph}
                                    </p>
                                </Reveal>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="bg-[#171717] grid-lines-dark py-20">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <Reveal>
                        <Eyebrow dark>Your Sector</Eyebrow>
                        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#F6F4EF] mt-4 leading-snug max-w-xl">
                            Whatever your sector — let's talk mandates.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link
                            to="/contact"
                            data-testid="industries-cta-contact"
                            className="inline-flex items-center gap-2 bg-[#C9A24B] hover:bg-[#D8BC7A] text-[#0C0C0C] font-semibold px-7 py-3.5 text-sm transition-colors duration-200 active:scale-[0.98]"
                        >
                            Partner With Us <ArrowUpRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
